import { useEffect, useState } from "react";

const NFC_WS_URL = "ws://localhost:8535/api/nfc/ws";

// ─── WebSocket singleton ──────────────────────────────────────────────────────
// One shared connection; all hook instances receive the same scan event.
const subscribers = new Set<(uuid: string) => void>();
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;

function broadcast(uuid: string) {
  subscribers.forEach((cb) => cb(uuid));
}

function connect() {
  intentionalClose = false;
  ws = new WebSocket(NFC_WS_URL);

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data as string);
      if (data.type === "card_scanned" && data.uuid) {
        broadcast(data.uuid as string);
      }
    } catch {}
  };

  ws.onclose = () => {
    ws = null;
    if (!intentionalClose && subscribers.size > 0) {
      reconnectTimer = setTimeout(connect, 5000);
    }
  };

  ws.onerror = () => {
    ws?.close();
  };
}

function ensureConnected() {
  if (
    subscribers.size > 0 &&
    (!ws ||
      ws.readyState === WebSocket.CLOSED ||
      ws.readyState === WebSocket.CLOSING)
  ) {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    connect();
  }
}

function maybeDisconnect() {
  if (subscribers.size === 0) {
    intentionalClose = true;
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    ws?.close();
    ws = null;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useNfcReader = (): string | null => {
  const [scannedCard, setScannedCard] = useState<string | null>(null);

  // Reset to null after consumers have reacted (same contract as before)
  useEffect(() => {
    if (scannedCard !== null) {
      setScannedCard(null);
    }
  }, [scannedCard]);

  // ── Source 1: Tray-app WebSocket ─────────────────────────────────────────
  useEffect(() => {
    const handleScan = (uuid: string) => setScannedCard(uuid);
    subscribers.add(handleScan);
    ensureConnected();

    return () => {
      subscribers.delete(handleScan);
      maybeDisconnect();
    };
  }, []);

  // ── Source 2: Keyboard / USB NFC reader (HID emulation) ─────────────────
  // Some readers send keystrokes ending with Enter. Multiple keys arriving
  // within 50 ms of each other (too fast for a human) identify a card scan.
  useEffect(() => {
    let keyBuffer = "";
    let lastKeyTime = 0;

    const handleKeyDown = (event: KeyboardEvent): void => {
      const now = Date.now();

      if (event.key !== "Enter") {
        if (lastKeyTime !== 0 && now - lastKeyTime > 1000) {
          keyBuffer = "";
        } else if (lastKeyTime !== 0 && now - lastKeyTime > 50) {
          // Too slow — treat as normal typing, not a card scan
          lastKeyTime = now;
          keyBuffer = event.key;
          return;
        }
        lastKeyTime = now;
        keyBuffer += event.key;
      } else {
        if (lastKeyTime !== 0 && now - lastKeyTime < 50) {
          // Fast Enter after rapid keystrokes → card scan
          const scanned = keyBuffer;
          keyBuffer = "";
          lastKeyTime = 0;

          // Blur any focused input so the card data isn't typed into it
          const active = document.activeElement as HTMLInputElement | null;
          if (active && typeof active.blur === "function") active.blur();

          setScannedCard(scanned);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return scannedCard;
};

export default useNfcReader;
